import {
  ObjectType,
  PaymentServicePaymentBatchStatus as PaymentBatchStatusEnum,
} from "#graphql/supergraphEnums";
import { toStandardFormat, formatCalendarDate } from "#utils/datetime_utils";
import { toDbId } from "../../../utils/graphql_utils";
import { isDiligenceNotConfirmed } from "../../PayableWorkflow/utils";
import { getCombinedStatusConfig } from "../../shared/combinedStatusUtil";
import { batchStatusUIConfig } from "../PaymentBatchesTable/util/batchStatusUtil";
import { paymentStatusUIConfig } from "../PaymentBatchesTable/util/paymentStatusUtils";
import { slideoutContainer, slideoutContainerWide } from "./styles_vanilla.css";
import {
  PaymentBatch,
  PaymentBatchDetails,
  PayablePaymentBatchDetails,
  PaymentBatchReviewInput,
  PaymentBatchReviewState,
} from "./types";

export type { PaymentBatchReviewInput, PaymentBatchReviewState };

export function getPaymentBatchReviewState(
  input: PaymentBatchReviewInput,
): PaymentBatchReviewState {
  const {
    isGpx,
    status,
    userId,
    creatorUserId,
    approverUserIds,
    hasApprovePaymentPermission,
    canReviewOwnBatches = false,
    requiredApprovalCount,
    watchlistStatus = false,
  } = input;

  let isAwaitingReview,
    hasUserAlreadyApproved,
    numApprovalsRequired,
    approvalsReceived,
    approvalsRemaining,
    canUserReviewBasedOnCreatorStatus;

  if (isGpx) {
    isAwaitingReview = status === PaymentBatchStatusEnum.ReadyForReview;

    hasUserAlreadyApproved = approverUserIds.includes(userId);

    numApprovalsRequired = requiredApprovalCount ?? 0;

    approvalsReceived = approverUserIds.length;

    approvalsRemaining = Math.max(0, numApprovalsRequired - approvalsReceived);

    canUserReviewBasedOnCreatorStatus = canReviewOwnBatches || userId !== creatorUserId;
  } else {
    isAwaitingReview = status === PaymentBatchStatusEnum.ReadyForTreasuryReview;

    // only 1 approval in canopy, so this is impossible
    hasUserAlreadyApproved = false;

    numApprovalsRequired = 1;

    approvalsReceived = isAwaitingReview ? 0 : 1;

    approvalsRemaining = numApprovalsRequired - approvalsReceived;

    canUserReviewBasedOnCreatorStatus = userId !== creatorUserId;
  }

  // Check if payee has pending screening review
  // Only applicable for payable batches; non-payable batches (e.g., distributions) don't have screening
  // If watchlistStatus is undefined/null, it means this is a non-payable batch, so screening doesn't apply
  const hasPayeesPendingScreening = watchlistStatus
    ? isDiligenceNotConfirmed(watchlistStatus)
    : false;

  const canUserSubmitReview =
    hasApprovePaymentPermission &&
    isAwaitingReview &&
    !hasUserAlreadyApproved &&
    canUserReviewBasedOnCreatorStatus &&
    !hasPayeesPendingScreening;

  const isFinalApproval = approvalsRemaining === 1;

  const isCreatorAndCannotReviewOwn = !canUserReviewBasedOnCreatorStatus;

  return {
    canUserSubmitReview,
    hasUserAlreadyApproved,
    isFullyReviewed: !isAwaitingReview,
    isFinalApproval,
    approvalsRemaining,
    numApprovalsRequired,
    approvalsReceived,
    hasApprovePaymentPermission,
    isCreatorAndCannotReviewOwn,
    hasPayeesPendingScreening,
  };
}

export const getContainerStyleFromPaymentBatch = (paymentBatch?: PaymentBatch): string => {
  const objectType = paymentBatch?.objectType;
  let containerStyleName = slideoutContainer;
  if (objectType === ObjectType.Payable) {
    containerStyleName = slideoutContainerWide;
  }
  return containerStyleName;
};

const getSinglePosition = (
  paymentBatch: PaymentBatch,
): { id: string; name: string | null } | null => {
  if (paymentBatch.objectsCount !== 1) return null;
  const position = paymentBatch.payments?.[0]?.position;
  return position ? { id: position.id, name: position.account?.name ?? null } : null;
};

export const mapPaymentBatchToPaymentBatchDetails = (
  paymentBatch: PaymentBatch,
  userId: number,
  hasApprovePaymentPermission: boolean,
  isGPX?: boolean,
  totalPositions?: number,
): PaymentBatchDetails => {
  // Extract scheduledProcessDate from the first payment (all payments share the same date)
  const firstPayment = paymentBatch.payments[0];
  const scheduledProcessDate: string | null | undefined =
    firstPayment && "scheduledProcessDate" in firstPayment
      ? (firstPayment.scheduledProcessDate as string | null | undefined)
      : null;

  // Compute reviewState using internal data
  const creatorUserId = toDbId(paymentBatch.creatorId);
  const approverUserIds = (paymentBatch.approvals ?? []).map((a) => toDbId(a.userId));
  const reviewState = getPaymentBatchReviewState({
    isGpx: isGPX ?? false,
    status: paymentBatch.status,
    userId,
    creatorUserId,
    approverUserIds,
    hasApprovePaymentPermission,
    canReviewOwnBatches: !paymentBatch.isDifferentApproverRequired,
    requiredApprovalCount: paymentBatch.requiredApprovalCount,
  });

  const refObj = paymentBatch.referenceObject;
  const hasPaymentsFields =
    refObj?.__typename === "CapitalCallBatchObjectType" ||
    refObj?.__typename === "DistributionBatchObjectType" ||
    refObj?.__typename === "TransactionBatchObjectType";

  const singlePosition = getSinglePosition(paymentBatch);

  return {
    id: paymentBatch.id,
    transactionDescription: hasPaymentsFields ? refObj.description : null,
    referenceObjectLabel: hasPaymentsFields ? refObj.paymentsLabel : null,
    referenceObjectSubtitleLabel: hasPaymentsFields ? refObj.paymentsSubtitleLabel : null,
    transactionBatchId: hasPaymentsFields ? refObj.id : undefined,
    objectType: paymentBatch.objectType,
    createdDate: toStandardFormat(paymentBatch.createdAt),
    scheduledProcessDate: formatCalendarDate(scheduledProcessDate),
    batchStatusConfig: batchStatusUIConfig(
      paymentBatch.status,
      paymentBatch.fundingPayment?.method,
    ),
    paymentStatus: paymentStatusUIConfig(paymentBatch.payments),
    combinedStatusConfig: getCombinedStatusConfig(
      paymentBatch.status,
      paymentBatch.payments,
      isGPX,
      paymentBatch.fundingPayment?.method,
    ),
    clientName: paymentBatch.arena.name,
    paymentMethod: paymentBatch.fundingPayment?.method,
    entityName: paymentBatch.entity.name,
    entityId: paymentBatch.entity.id,
    originatingAccount: `${paymentBatch.bankAccount?.bankName} ****${paymentBatch.bankAccount?.bankInfoRedacted}`,
    receivingAccount: paymentBatch.objectsCount,
    totalPositionsInDistribution:
      refObj?.__typename === "DistributionBatchObjectType"
        ? refObj.distributionCount ?? null
        : totalPositions ?? null,
    totalAmount: paymentBatch.netAmount,
    fundingPayment: paymentBatch.fundingPayment,
    payments: paymentBatch.payments,
    hostName: paymentBatch.arena.jsqHostname,
    paymentDescription: paymentBatch.paymentDescription,
    makerNote: paymentBatch.makerNote ?? null,
    bankAccount: paymentBatch.bankAccount,
    status: paymentBatch.status,
    approvals: paymentBatch.approvals ?? [],
    requiredApprovalCount: paymentBatch.requiredApprovalCount,
    isDifferentApproverRequired: paymentBatch.isDifferentApproverRequired,
    reviewState,
    positionCount: paymentBatch.objectsCount ?? undefined,
    singlePositionId: singlePosition?.id ?? null,
    singlePositionName: singlePosition?.name ?? null,

    requestedPaymentDate: hasPaymentsFields
      ? formatCalendarDate(refObj.requestedPaymentDate)
      : null,
  };
};

export const mapPaymentBatchToPayablePaymentBatchDetails = (
  paymentBatch: PaymentBatch,
  userId: number,
  hasApprovePaymentPermission: boolean,
  isGPX?: boolean,
): PayablePaymentBatchDetails => {
  const referenceObject = paymentBatch.referenceObject;
  const payableObject = paymentBatch.referenceObject;

  if (
    referenceObject?.__typename !== "PayableObjectType" ||
    !payableObject ||
    payableObject.__typename !== "PayableObjectType"
  ) {
    throw new Error("Payment batch does not have a PayableObjectType reference");
  }

  const scheduledProcessDate =
    paymentBatch.payments.length > 0 ? paymentBatch.payments[0].scheduledProcessDate : null;

  const invoiceNumber = payableObject.invoiceNumber;
  const invoiceDate = payableObject.invoiceDate;
  const dueDate = payableObject.dueDate;
  const amount = payableObject.amount;
  const payee = payableObject.payee;
  const supportingDocuments = payableObject.supportingDocuments;

  // Compute reviewState using internal data
  const creatorUserId = toDbId(paymentBatch.creatorId);
  const approverUserIds = (paymentBatch.approvals ?? []).map((a) => toDbId(a.userId));
  const reviewState = getPaymentBatchReviewState({
    isGpx: isGPX ?? false,
    status: paymentBatch.status,
    userId,
    creatorUserId,
    approverUserIds,
    hasApprovePaymentPermission,
    canReviewOwnBatches: !paymentBatch.isDifferentApproverRequired,
    requiredApprovalCount: paymentBatch.requiredApprovalCount,
    watchlistStatus: payee.paymentPref?.watchlistStatus,
  });

  const firstPayment = paymentBatch.payments[0];
  const referenceNumber = firstPayment?.referenceNumber ?? null;

  return {
    id: paymentBatch.id,
    payeeName: payee.name,
    payeeType: payee.payeeType,
    payeeId: payee.id,
    payeeStatus: payee.status,
    watchlistStatus: payee.paymentPref?.watchlistStatus,
    status: paymentBatch.status,
    combinedStatusConfig: getCombinedStatusConfig(
      paymentBatch.status,
      paymentBatch.payments,
      isGPX,
      paymentBatch.fundingPayment?.method,
    ),
    createdDate: toStandardFormat(paymentBatch.createdAt),
    paymentDate: scheduledProcessDate ? formatCalendarDate(scheduledProcessDate) ?? "" : "",
    invoiceDate: invoiceDate ? formatCalendarDate(String(invoiceDate)) ?? "" : "",
    dueDate: dueDate ? formatCalendarDate(String(dueDate)) ?? "" : "",
    bankAccount: paymentBatch.bankAccount,
    entity: {
      id: paymentBatch.entity.id,
      name: paymentBatch.entity.name,
    },
    hostName: paymentBatch.arena.jsqHostname,
    paymentAmount: paymentBatch.netAmount,
    paymentDescription: paymentBatch.paymentDescription,
    invoiceNumber,
    invoiceAmount: amount,
    supportingDocuments: supportingDocuments.map((doc) => ({
      id: doc.id,
      name: doc.name,
      viewUrl: doc.viewUrl,
    })),
    payments: paymentBatch.payments,
    reviewState,
    referenceNumber,
  };
};
